import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Switch } from '@headlessui/react';
import { EyeIcon, EyeSlashIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DEFAULT_API_BASE } from '../config';
import { useApiClient } from '../lib/api';
import { useAuth } from '../providers/auth-provider';
import { useConfig } from '../providers/config-provider';
import { useNotification } from '../providers/notification-provider';
import StatusPill from '../components/common/StatusPill';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { STAFF_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';

const schema = z.object({
  name: z.string().min(2, 'Informe o nome'),
  email: z.string().email('Email inválido').optional(),
  phone: z.string().optional(),
  role: z.string().optional(),
  password: z.string().optional(),
  isActive: z.boolean().optional()
});

const pageSize = 10;
const guidLikeRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeBase = (value) => {
  const trimmed = (value || '').trim().replace(/\/+$/, '');
  return trimmed.length ? trimmed : DEFAULT_API_BASE.replace(/\/+$/, '');
};

const StaffPage = () => {
  const api = useApiClient();
  const { user } = useAuth();
  const config = useConfig();
  const { showToast, showConfirm } = useNotification();

  const [staff, setStaff] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [presenceConnectionState, setPresenceConnectionState] = useState('disconnected');
  const connectionRef = useRef(null);
  const presenceEpochRef = useRef(0);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingMember, setEditingMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(STAFF_HELP_SECTIONS[0].key);

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: 'Waiter',
      password: '',
      isActive: true
    }
  });

  const roleOptions = useMemo(() => {
    const role = user?.role;
    if (role === 'AdminMaster') return ['AdminMaster', 'Admin', 'Manager', 'Waiter'];
    if (role === 'Admin') return ['Admin', 'Manager', 'Waiter'];
    if (role === 'Manager') return ['Manager', 'Waiter'];
    return [];
  }, [user?.role]);

  const roleLabel = (role) =>
    ({
      AdminMaster: 'Admin Master',
      Admin: 'Admin',
      Manager: 'Gerente',
      Waiter: 'Garçom'
    }[role] || role);

  const currentRoleOptions = useMemo(() => {
    if (modalMode === 'edit' && user?.role === 'Manager' && editingMember && editingMember.id === user.id) {
      return ['Manager'];
    }
    return roleOptions;
  }, [modalMode, user?.role, editingMember, roleOptions, user?.id]);

  useEffect(() => {
    document.title = 'Equipe | Orion WaiterCall';
  }, []);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/company/users', {
        params: {
          includeInactive,
          page,
          pageSize,
          search: searchTerm.trim() || undefined
        }
      });

      const payload = response.data;
      const list = payload?.items || payload || [];
      const filtered = user?.role === 'AdminMaster' ? list : list.filter((member) => member.role !== 'AdminMaster');
      setStaff(filtered);

      if (payload?.items) {
        setTotalItems(payload.totalItems ?? filtered.length);
        setTotalPages(Math.max(1, payload.totalPages ?? 1));
      } else {
        setTotalItems(filtered.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(error);
      showToast('Erro ao carregar equipe.', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, includeInactive, page, searchTerm, user?.role, showToast]);

  const loadProfileAndStaff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/company/profile');
      const list = response.data?.restaurants || [];
      if (user?.role === 'Manager') {
        setRestaurants(user?.restaurants || []);
      } else {
        setRestaurants(list.length ? list : user?.restaurants || []);
      }

      const currentRestaurantValid = config.restaurantId && list.some((restaurant) => restaurant.id === config.restaurantId);
      if (!currentRestaurantValid) {
        const fallback = list[0]?.id || null;
        config.setRestaurantId(fallback);
      }

      await loadStaff();
    } catch (error) {
      console.error(error);
      setLoading(false);
      showToast('Não foi possível carregar dados da empresa.', 'error');
    }
  }, [api, user?.restaurants, user?.role, config, loadStaff, showToast]);

  useEffect(() => {
    loadProfileAndStaff();
  }, [loadProfileAndStaff, config.companyId, config.restaurantId]);

  const openCreate = () => {
    if (!config.restaurantId) {
      showToast('Selecione um restaurante no topo.', 'warning');
      return;
    }

    setModalMode('create');
    setEditingMember(null);
    reset({
      name: '',
      email: '',
      phone: '',
      role: roleOptions[0] || 'Waiter',
      password: '',
      isActive: true
    });
    setSelectedRestaurants([]);
    setShowPassword(false);
    setModalOpen(true);
  };

  const openEdit = (member) => {
    if (!config.restaurantId) {
      showToast('Selecione um restaurante no topo.', 'warning');
      return;
    }

    if (user?.role === 'Manager' && !['Manager', 'Waiter'].includes(member.role)) {
      showToast('Gerentes não podem editar administradores.', 'warning');
      return;
    }

    setModalMode('edit');
    setEditingMember(member);
    const effectiveRole = member.role;
    reset({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role: effectiveRole,
      password: '',
      isActive: member.isActive
    });
    setSelectedRestaurants((member.restaurants || []).map((restaurant) => restaurant.id));
    setShowPassword(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
    setShowPassword(false);
  };

  const onSubmit = async (values) => {
    if (!config.restaurantId) {
      showToast('Selecione um restaurante no topo.', 'warning');
      return;
    }

    const willInactivate = modalMode === 'edit' && editingMember?.isActive && values.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar colaborador',
        message: `Deseja inativar ${editingMember.name}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      if (modalMode === 'edit' && editingMember) {
        await api.put(`/api/v1/company/users/${editingMember.id}`, {
          name: values.name,
          phone: values.phone || undefined,
          role: roleOptions.includes(values.role || '') ? values.role : undefined,
          password: values.password || undefined,
          isActive: values.isActive ?? true,
          restaurantIds: selectedRestaurants
        });
        showToast('Colaborador atualizado com sucesso.', 'success');
      } else {
        await api.post('/api/v1/company/users', {
          name: values.name,
          email: values.email || '',
          phone: values.phone || undefined,
          role: roleOptions.includes(values.role || '') ? values.role : 'Waiter',
          password: values.password || '',
          restaurantIds: selectedRestaurants
        });
        showToast('Colaborador cadastrado com sucesso.', 'success');
      }

      closeModal();
      loadStaff();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Não foi possível salvar o colaborador.';
      showToast(message, 'error');
      setSaving(false);
    }
  };

  const deleteMember = async (member) => {
    if (!config.restaurantId) {
      return;
    }

    const confirmed = await showConfirm({
      title: 'Excluir colaborador',
      message: `Deseja excluir ${member.name}?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar'
    });

    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(`/api/v1/company/users/${member.id}`);
      showToast('Colaborador excluído com sucesso.', 'success');

      loadStaff();
    } catch (error) {
      console.error(error);
      showToast('Não foi possível excluir o colaborador.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleRestaurantSelection = (id, checked) => {
    setSelectedRestaurants((current) => {
      if (checked) {
        return Array.from(new Set([...current, id]));
      }
      return current.filter((item) => item !== id);
    });
  };

  const allSelected = restaurants.length > 0 && selectedRestaurants.length === restaurants.length;

  const isAssignedToSelectedRestaurant = useCallback(
    (member) => (member.restaurants || []).some((restaurant) => restaurant.id === config.restaurantId),
    [config.restaurantId]
  );

  const getPresenceMeta = useCallback(
    (member) => {
      if (!config.restaurantId || !isAssignedToSelectedRestaurant(member)) {
        return {
          label: 'Sem vínculo',
          className: 'bg-slate-100 text-slate-600',
          dotClassName: 'bg-slate-400'
        };
      }

      if (onlineUserIds.has(member.id)) {
        return {
          label: 'Online',
          className: 'bg-emerald-100 text-emerald-700',
          dotClassName: 'bg-emerald-500'
        };
      }

      return {
        label: 'Offline',
        className: 'bg-gray-100 text-gray-600',
        dotClassName: 'bg-gray-400'
      };
    },
    [config.restaurantId, isAssignedToSelectedRestaurant, onlineUserIds]
  );

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const visibleStaff = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const roleOrder = { AdminMaster: 0, Admin: 1, Manager: 2, Waiter: 3 };
    const statusOrder = { Online: 0, Offline: 1, 'Sem vínculo': 2 };
    const toText = (value) => String(value || '').trim().toLocaleLowerCase('pt-BR');

    return [...staff].sort((a, b) => {
      if (sortConfig.key === 'isActive') {
        if (a.isActive !== b.isActive) {
          return (a.isActive ? -1 : 1) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      if (sortConfig.key === 'role') {
        const left = roleOrder[a.role] ?? 999;
        const right = roleOrder[b.role] ?? 999;
        if (left !== right) {
          return (left - right) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      if (sortConfig.key === 'connection') {
        const left = statusOrder[getPresenceMeta(a).label] ?? 999;
        const right = statusOrder[getPresenceMeta(b).label] ?? 999;
        if (left !== right) {
          return (left - right) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      const left = toText(sortConfig.key === 'email' ? a.email : a.name);
      const right = toText(sortConfig.key === 'email' ? b.email : b.name);
      const comparison = left.localeCompare(right, 'pt-BR') * direction;
      if (comparison !== 0) {
        return comparison;
      }

      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }, [getPresenceMeta, sortConfig.direction, sortConfig.key, staff]);

  const disconnectPresence = useCallback(() => {
    presenceEpochRef.current += 1;
    if (connectionRef.current) {
      const current = connectionRef.current;
      connectionRef.current = null;
      current.stop().catch(() => undefined);
    }

    setPresenceConnectionState('disconnected');
    setOnlineUserIds(new Set());
  }, []);

  const connectPresence = useCallback(async () => {
    disconnectPresence();

    if (!config.restaurantId) {
      return;
    }

    const currentEpoch = presenceEpochRef.current + 1;
    presenceEpochRef.current = currentEpoch;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${normalizeBase(config.apiBase)}/hubs/calls`, {
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('presence.changed', (payload) => {
      const userId = String(payload?.userId || '').trim();
      if (!guidLikeRegex.test(userId) || presenceEpochRef.current !== currentEpoch) {
        return;
      }

      setOnlineUserIds((current) => {
        const next = new Set(current);
        if (payload?.isOnline) {
          next.add(userId);
        } else {
          next.delete(userId);
        }
        return next;
      });
    });

    connection.onreconnecting(() => {
      setPresenceConnectionState('reconnecting');
    });

    connection.onreconnected(async () => {
      if (presenceEpochRef.current !== currentEpoch) {
        return;
      }

      setPresenceConnectionState('connected');
      try {
        await connection.invoke('JoinRestaurantGroup', config.restaurantId);
        const onlineUsers = await connection.invoke('GetOnlineUsers', config.restaurantId);
        if (presenceEpochRef.current !== currentEpoch) {
          return;
        }

        const validUsers = Array.isArray(onlineUsers)
          ? onlineUsers.filter((item) => guidLikeRegex.test(String(item || '').trim()))
          : [];
        setOnlineUserIds(new Set(validUsers));
      } catch {
        setOnlineUserIds(new Set());
      }
    });

    connection.onclose(() => {
      if (presenceEpochRef.current !== currentEpoch) {
        return;
      }

      setPresenceConnectionState('disconnected');
      setOnlineUserIds(new Set());
    });

    try {
      await connection.start();
      await connection.invoke('JoinRestaurantGroup', config.restaurantId);
      const onlineUsers = await connection.invoke('GetOnlineUsers', config.restaurantId);
      if (presenceEpochRef.current !== currentEpoch) {
        connection.stop().catch(() => undefined);
        return;
      }

      const validUsers = Array.isArray(onlineUsers)
        ? onlineUsers.filter((item) => guidLikeRegex.test(String(item || '').trim()))
        : [];
      setOnlineUserIds(new Set(validUsers));
      connectionRef.current = connection;
      setPresenceConnectionState('connected');
    } catch (error) {
      console.error(error);
      setPresenceConnectionState('disconnected');
      setOnlineUserIds(new Set());
    }
  }, [config.apiBase, config.restaurantId, disconnectPresence]);

  const refreshOnlineSnapshot = useCallback(async () => {
    if (!config.restaurantId || !connectionRef.current || presenceConnectionState !== 'connected') {
      return;
    }

    try {
      const onlineUsers = await connectionRef.current.invoke('GetOnlineUsers', config.restaurantId);
      const validUsers = Array.isArray(onlineUsers)
        ? onlineUsers.filter((item) => guidLikeRegex.test(String(item || '').trim()))
        : [];
      setOnlineUserIds(new Set(validUsers));
    } catch {
      // Keep last known state on transient failures.
    }
  }, [config.restaurantId, presenceConnectionState]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    connectPresence();
    return () => {
      disconnectPresence();
    };
  }, [connectPresence, disconnectPresence]);

  useEffect(() => {
    if (presenceConnectionState !== 'connected') {
      return undefined;
    }

    const timerId = setInterval(() => {
      refreshOnlineSnapshot();
    }, 30000);

    return () => {
      clearInterval(timerId);
    };
  }, [presenceConnectionState, refreshOnlineSnapshot]);

  return (
    <section>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Equipe</h1>
          <p className="mt-2 text-sm text-gray-700">Cadastre garçons e gestores com vínculo aos restaurantes.</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 sm:ml-16 sm:mt-0 sm:flex-none">
          <button type="button" onClick={openCreate} className="inline-flex flex-1 items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:flex-none">
            Novo colaborador
          </button>
          <ContextHelpButton
            label="Ajuda sobre equipe"
            onClick={() => {
              setHelpSectionKey('roles');
              setIsHelpOpen(true);
            }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
        <input
          type="text"
          placeholder="Buscar por nome, email ou função..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 max-w-md"
        />
        <div className="w-full sm:w-auto">
          <div className="flex h-[38px] items-center gap-3">
            <Switch
              checked={!includeInactive}
              onChange={(checked) => {
                setIncludeInactive(!checked);
                setPage(1);
              }}
              className={`${!includeInactive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
            >
              <span className="sr-only">Apenas ativos</span>
              <span
                aria-hidden="true"
                className={`${!includeInactive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="text-sm text-gray-600">Apenas ativos</span>
          </div>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto">
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${presenceConnectionState === 'connected' ? 'bg-emerald-100 text-emerald-700' : presenceConnectionState === 'reconnecting' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
            {presenceConnectionState === 'connected' ? 'Presença em tempo real ativa' : presenceConnectionState === 'reconnecting' ? 'Reconectando presença...' : 'Presença offline'}
          </span>
        </div>
      </div>

      {!config.restaurantId ? (
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm font-semibold text-gray-600">
          Selecione um restaurante no topo para visualizar a equipe.
        </div>
      ) : (
        <>
          <div className="mt-6 sm:hidden space-y-3">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Carregando equipe...</div>
            ) : visibleStaff.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Nenhum colaborador encontrado.</div>
            ) : (
              visibleStaff.map((member) => (
                <article key={member.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{member.name}</h3>
                      <p className="text-xs text-gray-600">{member.email}</p>
                      {member.phone ? <p className="text-xs text-gray-500">Telefone: {member.phone}</p> : null}
                    </div>
                    <StatusPill label={member.isActive ? 'Ativo' : 'Inativo'} variant={member.isActive ? 'success' : 'muted'} />
                  </div>
                  <div className="mt-2">
                    <StatusPill label={roleLabel(member.role)} variant="info" />
                  </div>
                  <div className="mt-2">
                    {(() => {
                      const presence = getPresenceMeta(member);
                      return (
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${presence.className}`}>
                          <span className={`h-2 w-2 rounded-full ${presence.dotClassName}`} />
                          {presence.label}
                        </span>
                      );
                    })()}
                  </div>
                  {member.restaurants?.length ? (
                    <p className="mt-2 text-xs text-gray-500">Restaurantes: {member.restaurants.map((restaurant) => restaurant.name).join(', ')}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setViewingMember(member)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Ver</button>
                    <button type="button" onClick={() => openEdit(member)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Editar</button>
                    <button type="button" onClick={() => deleteMember(member)} disabled={saving} className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
                      <TrashIcon className="h-4 w-4" />
                      Excluir
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>

          <div className="mt-8 hidden sm:block">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  {loading ? (
                    <div className="bg-white p-4 text-sm text-gray-600">Carregando equipe...</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-300 bg-white">
                      <thead className="bg-gray-50">
                        <tr>
                          <SortableTh
                            label="Nome"
                            sortKey="name"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          />
                          <SortableTh
                            label="Email"
                            sortKey="email"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          />
                          <SortableTh
                            label="Função"
                            sortKey="role"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          />
                          <SortableTh
                            label="Status"
                            sortKey="isActive"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          />
                          <SortableTh
                            label="Conexão"
                            sortKey="connection"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          />
                          <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Ações</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {visibleStaff.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-8 text-center text-sm text-gray-500">Nenhum colaborador encontrado com os filtros atuais.</td>
                          </tr>
                        ) : (
                          visibleStaff.map((member) => (
                            <tr key={member.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewingMember(member)}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{member.name}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{member.email}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{roleLabel(member.role)}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <StatusPill label={member.isActive ? 'Ativo' : 'Inativo'} variant={member.isActive ? 'success' : 'muted'} />
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {(() => {
                                  const presence = getPresenceMeta(member);
                                  return (
                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${presence.className}`}>
                                      <span className={`h-2 w-2 rounded-full ${presence.dotClassName}`} />
                                      {presence.label}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(member);
                                  }}
                                  title="Editar"
                                  className="mr-4 text-indigo-600 hover:text-indigo-900"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteMember(member);
                                  }}
                                  title="Excluir"
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}

                  {!loading && totalItems > 0 ? (
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      totalItems={totalItems}
                      pageSize={pageSize}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={modalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded bg-white p-4 sm:p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">
              {modalMode === 'edit' ? 'Editar colaborador' : 'Novo colaborador'}
            </Dialog.Title>

            <form className="grid gap-4 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1">
                <label className="text-sm font-medium leading-6 text-gray-900">Nome *</label>
                <input type="text" {...register('name')} className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
                {errors.name ? <p className="text-sm text-red-600">{errors.name.message}</p> : null}
              </div>

              {modalMode === 'create' ? (
                <div className="space-y-1">
                  <label className="text-sm font-medium leading-6 text-gray-900">Email *</label>
                  <input type="email" {...register('email')} className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
                  {errors.email ? <p className="text-sm text-red-600">{errors.email.message}</p> : null}
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="text-sm font-medium leading-6 text-gray-900">Email</label>
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">{editingMember?.email || '-'}</div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-sm font-medium leading-6 text-gray-900">Telefone</label>
                <input type="text" {...register('phone')} className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium leading-6 text-gray-900">Função</label>
                <select
                  {...register('role')}
                  disabled={currentRoleOptions.length === 0 || (user?.role === 'Manager' && editingMember && editingMember.id === user.id)}
                  className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                >
                  {currentRoleOptions.map((role) => (
                    <option key={role} value={role}>{roleLabel(role)}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium leading-6 text-gray-900">Senha {modalMode === 'create' ? '*' : '(opcional)'}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    className="w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition hover:text-gray-600"
                    aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="md:col-span-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">Vincular restaurantes</p>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => setSelectedRestaurants(e.target.checked ? restaurants.map((restaurant) => restaurant.id) : [])}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Todos
                  </label>
                </div>
                <div className="grid max-h-36 grid-cols-1 gap-2 overflow-auto">
                  {restaurants.map((restaurant) => (
                    <label key={restaurant.id} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm">
                      <span className="font-medium text-gray-900">{restaurant.name}</span>
                      <input
                        type="checkbox"
                        checked={selectedRestaurants.includes(restaurant.id)}
                        onChange={(e) => toggleRestaurantSelection(restaurant.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </label>
                  ))}
                  {!restaurants.length ? <p className="text-xs font-medium text-gray-600">Nenhum restaurante disponível.</p> : null}
                </div>
              </div>

              {modalMode === 'edit' ? (
                <label className="md:col-span-2 flex items-center gap-2 text-sm font-medium text-gray-900">
                  <input type="checkbox" {...register('isActive')} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  Colaborador ativo
                </label>
              ) : null}

              <div className="md:col-span-2 flex justify-end gap-2 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={Boolean(viewingMember)} onClose={() => setViewingMember(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded bg-white p-4 sm:p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Detalhes do colaborador</Dialog.Title>

            {viewingMember ? (
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Nome</p>
                  <p className="text-base font-medium text-gray-900">{viewingMember.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Email</p>
                  <p>{viewingMember.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Telefone</p>
                  <p>{viewingMember.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Função</p>
                  <p>{roleLabel(viewingMember.role)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Status</p>
                  <StatusPill label={viewingMember.isActive ? 'Ativo' : 'Inativo'} variant={viewingMember.isActive ? 'success' : 'muted'} />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Conexão</p>
                  {(() => {
                    const presence = getPresenceMeta(viewingMember);
                    return (
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${presence.className}`}>
                        <span className={`h-2 w-2 rounded-full ${presence.dotClassName}`} />
                        {presence.label}
                      </span>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Restaurantes</p>
                  <p>{viewingMember.restaurants?.length ? viewingMember.restaurants.map((restaurant) => restaurant.name).join(', ') : 'Sem vínculo'}</p>
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
              <button type="button" onClick={() => setViewingMember(null)} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Fechar</button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Equipe"
        description="Revise funções, vínculo com restaurantes, presença e cuidados no cadastro de usuários."
        sections={STAFF_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default StaffPage;
